import { type Cluster, type ClusterStats, MarkerClusterer, type Renderer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import settings from '/@/core/settings';
import colors from '../theme-provider/colors.tsx';

// Only markers that overlap on screen get grouped; from this zoom on (field level) they are always shown individually
const CLUSTER_RADIUS_PX = 40;
const CLUSTER_MAX_ZOOM = 13;

const clusterRenderer: Renderer = {
    render: ({ count, position, bounds }: Cluster, _stats: ClusterStats, map: google.maps.Map) => {
        const color = colors.themes[settings.app?.theme]?.mui?.primary?.primary || '#1976d2';
        const size = count < 10 ? 36 : count < 100 ? 42 : 48;
        const element = document.createElement('div');
        element.textContent = String(count);
        Object.assign(element.style, {
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            background: color,
            border: '2px solid #FFFFFF',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.4)',
            color: '#FFFFFF',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transform: 'translateY(50%)',
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            content: element,
            title: `${count}`,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            // Required for `gmp-click` listeners added with addEventListener
            gmpClickable: true,
        });
        // Zoom into the cluster on click (replaces the library's default handler, see createFieldClusterer)
        marker.addEventListener('gmp-click', () => {
            if (bounds) map.fitBounds(bounds);
        });

        return marker;
    },
};

/**
 * Creates a clusterer for field markers. Markers added to it must be created without `map`:
 * the clusterer decides which ones are shown.
 */
export const createFieldClusterer = (map: google.maps.Map) =>
    new MarkerClusterer({
        map,
        algorithm: new SuperClusterAlgorithm({ radius: CLUSTER_RADIUS_PX, maxZoom: CLUSTER_MAX_ZOOM }),
        renderer: clusterRenderer,
        // The library wires its click handler with `addListener('gmp-click')`, which Maps flags as deprecated on
        // AdvancedMarkerElement; null disables it (undefined would fall back to the default) and the renderer handles clicks
        onClusterClick: null as unknown as undefined,
    });
